import React, { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardHeader, CardContent } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Badge } from '../components/ui/Badge'
import { Modal } from '../components/ui/Modal'
import { userService } from '../services/userService'
import { PlusIcon, SearchIcon, PencilIcon, TrashIcon, CreditCardIcon } from 'lucide-react'
import toast from 'react-hot-toast'

interface UserStats {
  total_users?: number
  active_users?: number
  new_users_this_month?: number
}

export const UsersPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const queryClient = useQueryClient()

  // Fetch users
  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', { search: searchTerm }],
    queryFn: () => userService.getUsers({ search: searchTerm, limit: 50 }),
  })

  // Fetch user stats
  const { data: statsData } = useQuery({
    queryKey: ['user-stats'],
    queryFn: () => userService.getUserStats(),
  })

  const users = usersData?.data?.data || []
  const stats: UserStats = statsData?.data || {}

  const handleDeleteUser = async (userId: number, userName: string) => {
    if (confirm(`Är du säker på att du vill ta bort användaren "${userName}"?`)) {
      try {
        await userService.deleteUser(userId)
        queryClient.invalidateQueries({ queryKey: ['users'] })
        toast.success('Användare borttagen!')
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Kunde inte ta bort användare')
      }
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Användare</h1>
          <p className="text-gray-600">Hantera användare och deras träningspass</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <PlusIcon className="h-4 w-4 mr-2" />
          Lägg till användare
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Totalt användare</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total_users || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                <div className="h-6 w-6 rounded-full bg-primary-500"></div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Aktiva användare</p>
                <p className="text-3xl font-bold text-gray-900">{stats.active_users || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-success-100 flex items-center justify-center">
                <div className="h-6 w-6 rounded-full bg-success-500"></div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Nya denna månad</p>
                <p className="text-3xl font-bold text-gray-900">{stats.new_users_this_month || 0}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-warning-100 flex items-center justify-center">
                <div className="h-6 w-6 rounded-full bg-warning-500"></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Users List */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Användarlista</h3>
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Sök användare..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="space-y-2">
                      <div className="w-32 h-4 bg-gray-200 rounded"></div>
                      <div className="w-24 h-3 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <div className="w-16 h-8 bg-gray-200 rounded"></div>
                    <div className="w-16 h-8 bg-gray-200 rounded"></div>
                    <div className="w-16 h-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500 mb-4">
                {searchTerm ? 'Inga användare hittades' : 'Inga användare registrerade'}
              </div>
              {searchTerm && (
                <Button variant="outline" onClick={() => setSearchTerm('')}>
                  Rensa sökning
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {users.map((user) => (
                <div
                  key={user.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <div className="flex items-center space-x-4">
                    {/* Avatar */}
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-700">
                        {user.namn.charAt(0).toUpperCase()}
                      </span>
                    </div>

                    {/* User Info */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900">{user.namn}</h4>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        {user.epost && <span>{user.epost}</span>}
                        {user.telefon && <span>{user.telefon}</span>}
                        <span>{user.vouchers} pass kvar</span>
                        <span>{user.completed_trainings} genomförda</span>
                      </div>
                    </div>

                    {/* Badges */}
                    <div className="flex space-x-1">
                      {user.one_time_customer && (
                        <Badge variant="warning">Engångskund</Badge>
                      )}
                      {user.next_training_free && (
                        <Badge variant="success">Nästa gratis</Badge>
                      )}
                      {user.first_training_received && (
                        <Badge variant="info">Första genomförd</Badge>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm">
                      <CreditCardIcon className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm">
                      <PencilIcon className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteUser(user.id, user.namn)}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create User Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Lägg till användare"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Användarhantering kommer snart. Denna funktion är under utveckling.
          </p>
          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={() => setIsCreateModalOpen(false)}>
              Stäng
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
